import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router';

import { BackToLoginLink } from './common/BackToLoginLink';

interface SsoErrorInfo {
    titleId: string;
    descriptionId: string;
    title: string;
    description: string;
}

type SsoErrorCode =
    | 'ServerError'
    | 'InvalidClaims'
    | 'InvalidToken'
    | 'UserNotFound'
    | 'CompanyNotFound'
    | 'SsoNotEnabled'
    | 'InvalidIssuer'
    | 'DuplicateEmail'
    | 'UserDeactivated'
    | 'PartnerNotEligible';

const errorMessages: Record<SsoErrorCode, SsoErrorInfo> = {
    ServerError: {
        titleId: 'login.sso.error.serverError.title',
        descriptionId: 'login.sso.error.serverError.description',
        title: 'Something went wrong',
        description: 'An unexpected error occurred. Please try again or contact support.',
    },
    InvalidClaims: {
        titleId: 'login.sso.error.invalidClaims.title',
        descriptionId: 'login.sso.error.invalidClaims.description',
        title: 'Invalid claims',
        description:
            'The identity provider returned invalid claims. ' +
            'Please contact your administrator.',
    },
    InvalidToken: {
        titleId: 'login.sso.error.invalidToken.title',
        descriptionId: 'login.sso.error.invalidToken.description',
        title: 'Invalid token',
        description: 'The authentication token is invalid. Please try again.',
    },
    UserNotFound: {
        titleId: 'login.sso.error.userNotFound.title',
        descriptionId: 'login.sso.error.userNotFound.description',
        title: 'User not found',
        description:
            'No SalesScreen account was found for your identity. ' +
            'Please contact your administrator.',
    },
    CompanyNotFound: {
        titleId: 'login.sso.error.companyNotFound.title',
        descriptionId: 'login.sso.error.companyNotFound.description',
        title: 'Company not found',
        description:
            'No SalesScreen company was found for your organization. ' + 'Please contact support.',
    },
    SsoNotEnabled: {
        titleId: 'login.sso.error.ssoNotEnabled.title',
        descriptionId: 'login.sso.error.ssoNotEnabled.description',
        title: 'SSO not enabled',
        description:
            'Single sign-on is not enabled for your organization. ' +
            'Please contact your administrator.',
    },
    InvalidIssuer: {
        titleId: 'login.sso.error.invalidIssuer.title',
        descriptionId: 'login.sso.error.invalidIssuer.description',
        title: 'Invalid issuer',
        description:
            'The identity provider is not recognized. ' + 'Please contact your administrator.',
    },
    DuplicateEmail: {
        titleId: 'login.sso.error.duplicateEmail.title',
        descriptionId: 'login.sso.error.duplicateEmail.description',
        title: 'Duplicate email',
        description: 'Multiple accounts share this email address. ' + 'Please contact support.',
    },
    UserDeactivated: {
        titleId: 'login.sso.error.userDeactivated.title',
        descriptionId: 'login.sso.error.userDeactivated.description',
        title: 'Account deactivated',
        description:
            'Your SalesScreen account has been deactivated. ' +
            'Please contact your administrator.',
    },
    PartnerNotEligible: {
        titleId: 'login.sso.error.partnerNotEligible.title',
        descriptionId: 'login.sso.error.partnerNotEligible.description',
        title: 'Not eligible for Curiocity',
        description:
            'Your account is not currently eligible for the Curiocity program. ' +
            'If you believe this is a mistake, please contact your HP program representative.',
    },
};

export const FailedSsoView = () => {
    const { provider } = useParams<{ provider: string }>();
    // The route segment is the provider name; the backend sends the error code as a query param.
    const error = new URLSearchParams(useLocation().search).get('error');

    const errorCode = (error ?? 'ServerError') as SsoErrorCode;
    const errorInfo = errorMessages[errorCode] ?? errorMessages.ServerError;

    if (provider === 'NINAA') {
        return <FailedNINAAView />;
    }

    return (
        <div>
            <h2 className="tw-text-l tw-font-semibold tw-mb-8">
                <FormattedMessage id={errorInfo.titleId} defaultMessage={errorInfo.title} />
            </h2>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
                <FormattedMessage
                    id={errorInfo.descriptionId}
                    defaultMessage={errorInfo.description}
                />
            </p>
            <BackToLoginLink />
        </div>
    );
};

const FailedNINAAView = () => (
    <div>
        <h2 className="tw-text-l tw-font-semibold tw-mb-8">
            <FormattedMessage id="login.sso.error.ninaa.title" defaultMessage="Unable to sign in" />
        </h2>
        <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
            <FormattedMessage
                id="login.sso.error.ninaa.description"
                defaultMessage="We could not complete your sign-in with Nordea NINAA. Please try again or contact support."
            />
        </p>
        <BackToLoginLink />
    </div>
);
